import { Component, OnInit, Input, Output, EventEmitter, SimpleChanges, OnChanges } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// cc-common imports
import { CCLocalStorageCategory, CCLocalStorageService, Utils } from 'cc-common';

// Service imports
import { ScreenService } from '../../../_services/shared-services/screen.service';
import { CiraHelpService } from '../../../_services/shared-services/cira-help.service';

// Constants
import { StorageKeyConstants } from '../../../_constants/storage-key-constants';
import { Constants } from '../../../_constants/constants';
import { WorkflowConstants } from '../../../shared-workflow-module/_constants/worflow-constants';
import { CiraHelpKeyConstants } from '../../../_constants/cira-help-key-constants';

@Component({
    selector: 'app-workflow-editor',
    templateUrl: './workflow-editor.component.html',
    styleUrls: ['./workflow-editor.component.scss']
})
export class WorkflowEditorComponent implements OnInit, OnChanges {
    @Input() workflowId: number;
    @Input() communityId: number;
    @Input() status: string;
    @Input() isPopupMode = false;

    @Output() closeParentContainer = new EventEmitter<boolean>();

    isSmallScreen = false;
    settingsMenu: object[];
    pdfViewerToLeft = false;
    pdfOrder: number;
    invoiceOrder: number;
    separateWindow = false;
    pdfURL: string = null;
    pdfLoader: boolean;

    formPanelWidth: string;
    pdfViewerWidth: string;
    helpLink: string;

    hideTitle = false;

    constructor(
        private readonly screenService: ScreenService,
        private readonly localStorageService: CCLocalStorageService,
        private readonly ciraHelpService: CiraHelpService,
        private readonly route: ActivatedRoute
    ) {
        this.helpLink = this.ciraHelpService.getHelpLinkByKey(CiraHelpKeyConstants.aapWfProcess);
    }

    ngOnInit() {
        this.isSmallScreen = this.screenService.isSmallScreen();
        this.pdfOrder = 1;
        this.invoiceOrder = 2;

        this.screenService.changed.subscribe(() => {
            this.isSmallScreen = this.screenService.isSmallScreen();
        });

        this.updateSplitterPanelWidth();

        if (Utils.isNullOrEmpty(this.workflowId)) {
            this.pdfURL = null;
            this.workflowId = null;
            this.communityId = null;
        }

        this.route.queryParams.subscribe((result) => {
            if (Utils.notNullOrEmpty(result)) {
                this.communityId = Utils.notNullAndDefined(result.communityId)
                    ? parseInt(result.communityId)
                    : this.communityId;
                this.workflowId = Utils.notNullAndDefined(result.workflowId)
                    ? parseInt(result.workflowId)
                    : this.workflowId;
                this.status = Utils.notNullAndDefined(result.workflowStatus) ? result.workflowStatus : this.status;
                this.isPopupMode = Utils.notNullAndDefined(result.isPopupMode)
                    ? Utils.equalsIgnoreCase(result.isPopupMode, 'true')
                    : this.isPopupMode;
                this.hideTitle = true;
            }
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (Utils.notNullAndDefined(changes.workflowId) && Utils.notNullAndDefined(changes.workflowId.currentValue)) {
            this.workflowId = changes.workflowId.currentValue;
        }
    }

    getScreenByWidth = (width: number): string => {
        return Utils.isTrue(this.isSmallScreen) ? 'sm' : 'lg';
    };

    onPdfURL(value: string): void {
        if (Utils.notNullOrEmpty(value)) {
            this.pdfURL = value;
        } else {
            this.pdfURL = null;
        }
    }

    onPdfLoader(event: boolean): void {
        setTimeout(() => {
            this.pdfLoader = event;
        }, 250);
    }

    onSplitterDragStart(): void {
        this.pdfLoader = true;
    }

    onSplitterDragEnd(event: any): void {
        this.pdfLoader = false;
        const sizes = event.sizes;

        this.pdfViewerWidth = sizes[this.pdfOrder - 1];
        this.formPanelWidth = sizes[this.invoiceOrder - 1];

        this.setSplitterPanelWidth();
    }

    onSplitterPositionChange(pdfToLeft: boolean): void {
        setTimeout(() => {
            this.pdfViewerToLeft = pdfToLeft;
            if (Utils.isTrue(this.pdfViewerToLeft)) {
                this.pdfOrder = 1;
                this.invoiceOrder = 2;
            } else {
                this.pdfOrder = 2;
                this.invoiceOrder = 1;
            }
            this.setSplitterContentPosition();
        }, 100);
    }

    onHideSplitter(separateWindow: boolean): void {
        setTimeout(() => {
            this.separateWindow = separateWindow;

            if (Utils.isTrue(this.separateWindow)) {
                this.invoiceOrder = 1;
                this.pdfOrder = 0;
            } else {
                if (Utils.isTrue(this.pdfViewerToLeft)) {
                    this.pdfOrder = 1;
                    this.invoiceOrder = 2;
                } else {
                    this.pdfOrder = 2;
                    this.invoiceOrder = 1;
                }
            }
        }, 100);
    }

    onEditorClose(forceReload: boolean): void {
        if (
            Utils.notNullOrEmpty(window.frameElement) &&
            Utils.notNullOrEmpty((window.frameElement as any).onWorkflowEditorFinish)
        ) {
            (window.frameElement as any).onWorkflowEditorFinish(true);
        }

        this.closeWorkflowEditor(forceReload);
    }

    isDataEntryStep(): boolean {
        let isDataEntry = false;

        if (
            (Utils.isNullOrEmpty(this.status) && Utils.isNullOrEmpty(this.workflowId)) ||
            Utils.equalsIgnoreCase(this.status, WorkflowConstants.SCANNED) ||
            Utils.equalsIgnoreCase(this.status, WorkflowConstants.AAPVERIFY)
        ) {
            isDataEntry = true;
        }

        return isDataEntry;
    }

    isSupervisorStep(): boolean {
        let isSupervisor = false;
        if (Utils.notNullOrEmpty(this.status) && Utils.equalsIgnoreCase(this.status, WorkflowConstants.AAPSUPERVISOR)) {
            isSupervisor = true;
        }
        return isSupervisor;
    }

    isCAMInbox(): boolean {
        let isCAMInbox = false;
        if (
            Utils.notNullOrEmpty(this.status) &&
            (Utils.equalsIgnoreCase(this.status, WorkflowConstants.CAMINBOX) ||
                Utils.equalsIgnoreCase(this.status, WorkflowConstants.CAMINREVIEW))
        ) {
            isCAMInbox = true;
        }
        return isCAMInbox;
    }

    isAccountantStep(): boolean {
        let isAccountantStep = false;
        if (
            Utils.notNullOrEmpty(this.status) &&
            (Utils.equalsIgnoreCase(this.status, WorkflowConstants.ACCTINREVIEW) ||
                Utils.equalsIgnoreCase(this.status, WorkflowConstants.ACCTINBOX) ||
                Utils.equalsIgnoreCase(this.status, WorkflowConstants.HOLD))
        ) {
            isAccountantStep = true;
        }
        return isAccountantStep;
    }

    isApprovedStep(): boolean {
        let isApprovedStep = false;
        if (Utils.notNullOrEmpty(this.status) && Utils.equalsIgnoreCase(this.status, WorkflowConstants.APPROVED)) {
            isApprovedStep = true;
        }
        return isApprovedStep;
    }

    isDeniedStep(): boolean {
        let isDeniedStep = false;
        if (Utils.notNullOrEmpty(this.status) && Utils.equalsIgnoreCase(this.status, WorkflowConstants.DENIED)) {
            isDeniedStep = true;
        }
        return isDeniedStep;
    }

    private closeWorkflowEditor(forceReload: boolean): void {
        this.pdfURL = null;
        this.closeParentContainer.emit(forceReload);
    }

    private updateSplitterPanelWidth(): void {
        this.formPanelWidth =
            this.localStorageService.getItem(
                StorageKeyConstants.wfEditorFormPanelWidthInPercent,
                CCLocalStorageCategory.WFE
            ) || Constants.splitterFormWidth;
        this.pdfViewerWidth =
            this.localStorageService.getItem(
                StorageKeyConstants.wfInvoicePanelWidthInPercent,
                CCLocalStorageCategory.WFE
            ) || Constants.splitterPDFViewerWidth;

        this.setSplitterPanelWidth();
    }

    private setSplitterPanelWidth(): void {
        this.localStorageService.setItem(
            StorageKeyConstants.wfEditorFormPanelWidthInPercent,
            this.formPanelWidth,
            CCLocalStorageCategory.WFE
        );
        this.localStorageService.setItem(
            StorageKeyConstants.wfInvoicePanelWidthInPercent,
            this.pdfViewerWidth,
            CCLocalStorageCategory.WFE
        );
    }

    private setSplitterContentPosition(): void {
        this.localStorageService.setItem(
            StorageKeyConstants.wfInvoiceViewerPosition,
            this.pdfViewerToLeft.toString(),
            CCLocalStorageCategory.WFE
        );
    }
}
